import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@shared';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';

interface WorkspaceSummary {
  id: string;
  name: string;
}

interface WorkspaceSelectorProps {
  workspaces: ReadonlyArray<WorkspaceSummary>;
  activeWorkspaceId: string | null;
  onSwitch: (workspaceId: string) => void | Promise<void>;
  variant?: 'sidebar' | 'header';
}

const initialsFor = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((segment) => segment[0]?.toUpperCase() ?? '')
    .join('') || '?';

export function WorkspaceSelector({
  workspaces,
  activeWorkspaceId,
  onSwitch,
  variant = 'sidebar',
}: WorkspaceSelectorProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Fall back to the first workspace when the active id is missing or
  // points at a workspace the user is no longer a member of. This can
  // happen right after a backend re-seed or when an invite gets revoked.
  const active =
    workspaces.find((w) => w.id === activeWorkspaceId) ?? workspaces[0] ?? null;

  if (!active) {
    return (
      <button
        onClick={() => navigate('/onboarding/workspace')}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm font-medium',
          variant === 'header'
            ? 'border-border text-muted-foreground hover:bg-muted'
            : 'border-sidebar-border text-sidebar-muted hover:bg-white/5',
        )}
      >
        <Plus className="h-4 w-4" />
        Create your workspace
      </button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex w-full min-w-0 items-center justify-between rounded-lg border px-2.5 py-2 text-sm font-medium',
            variant === 'header'
              ? 'border-border bg-muted/50 text-foreground shadow-sm hover:bg-muted'
              : 'border-sidebar-border bg-white/5 text-sidebar-foreground hover:bg-white/10',
            switching && 'opacity-60',
          )}
          disabled={switching}
          aria-label="Select workspace"
          data-testid="workspace-selector-trigger"
        >
          <div className="flex min-w-0 items-center gap-2">
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold',
                variant === 'header'
                  ? 'bg-primary/15 text-primary'
                  : 'bg-sidebar-active text-white',
              )}
            >
              {initialsFor(active.name)}
            </div>
            <span className="truncate">{active.name}</span>
          </div>
          <ChevronDown
            className={cn(
              'ml-2 h-4 w-4 shrink-0',
              variant === 'header' ? 'text-muted-foreground' : 'text-sidebar-muted',
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="start">
        <DropdownMenuLabel>Your workspaces</DropdownMenuLabel>
        {workspaces.map((workspace) => (
          <DropdownMenuItem
            key={workspace.id}
            onSelect={async () => {
              if (workspace.id === active.id) return;
              setSwitching(true);
              try {
                await onSwitch(workspace.id);
              } finally {
                setSwitching(false);
              }
            }}
            data-testid={`workspace-option-${workspace.id}`}
          >
            <div className="flex h-5 w-5 items-center justify-center rounded bg-sidebar-active text-xs font-bold text-white">
              {initialsFor(workspace.name)}
            </div>
            <span className="truncate">{workspace.name}</span>
            {workspace.id === active.id ? (
              <span className="ml-auto text-xs text-muted-foreground">current</span>
            ) : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => navigate('/workspace/' + active.id + '/settings/members')}
          data-testid="workspace-manage-link"
        >
          Manage members
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate('/onboarding/workspace')}
          data-testid="workspace-create-new"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create new workspace
        </DropdownMenuItem>
        {user ? (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              Signed in as <span className="font-medium text-foreground">{user.name}</span>
            </div>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
